import { useEffect, useState } from 'react'
import consumer from '@/lib/cable'
import type { BizminerAnalysisWebhookEvent, BizminerInsightRequest, BizminerPillarInsight } from '@/types/serializers'

interface InsightRequestUpdate {
  insight_request: BizminerInsightRequest
  pillar_insight?: BizminerPillarInsight
  webhook_event?: BizminerAnalysisWebhookEvent
}

interface UseInsightRequestChannelReturn {
  insightRequest: BizminerInsightRequest | null
  newEvents: BizminerAnalysisWebhookEvent[]
  isConnected: boolean
}

export function useInsightRequestChannel(
  initialInsightRequest: BizminerInsightRequest
): UseInsightRequestChannelReturn {
  const [insightRequest, setInsightRequest] = useState<BizminerInsightRequest>(initialInsightRequest)
  const [newEvents, setNewEvents] = useState<BizminerAnalysisWebhookEvent[]>([])
  const [isConnected, setIsConnected] = useState(false)

  useEffect(() => {
    // Don't subscribe if not in browser or no insight request
    if (!consumer || !insightRequest?.id) {
      return
    }

    const subscription = consumer.subscriptions.create(
      {
        channel: 'Bizminer::InsightRequestChannel',
        insight_request_id: insightRequest.id
      },
      {
        connected() {
          console.log(`Connected to InsightRequestChannel for request ${insightRequest.id}`)
          setIsConnected(true)
        },

        disconnected() {
          console.log(`Disconnected from InsightRequestChannel for request ${insightRequest.id}`)
          setIsConnected(false)
        },

        received(data: InsightRequestUpdate) {
          console.log('Received InsightRequest update:', data)

          // Update the insight request state
          setInsightRequest(data.insight_request)

          // Accumulate new webhook events
          if (data.webhook_event) {
            setNewEvents(prev => [data.webhook_event, ...prev])
          }
        }
      }
    )

    // Cleanup subscription on unmount or when insight request ID changes
    return () => {
      subscription.unsubscribe()
    }
  }, [insightRequest.id])

  return {
    insightRequest,
    newEvents,
    isConnected
  }
}